import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  COMPONENT_STATE_ARGS,
  getDefaultValueFromMetadata,
  pickArgs,
} from '../../../../utils/spds/metadata';
import { BOOLEAN, STRING } from '../../../../utils/component-validations';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { reads } from 'macro-decorators';
import { tracked } from '@glimmer/tracking';
import Button from '../button.gjs';
import Super from '../base.gjs';
import styles from './item.module.scss';

const METADATA = {
  component: 'Spds::Core::Menu::Item',
  description: 'Menu item component used within menus.',
  name: 'Menu Item',
  meta: {
    ...pickArgs(COMPONENT_STATE_ARGS, ['disabled', 'hidden']).meta,
    onClick: {
      ...ACTION_ARGS.meta.onClick,
      description: 'Action to be called when the menu item is clicked.',
      required: true,
    },
    retainSelection: {
      type: BOOLEAN,
      description: 'Indicates whether item will remain selected after second click',
      defaultValue: false,
    },
    role: {
      ...ACCESSIBILITY_ARGS.meta.role,
      defaultValue: 'option',
    },
    showSelectedStyles: {
      type: BOOLEAN,
      defaultValue: true,
      description:
        'Determines whether to apply styles for selected state when the item is selected',
    },
    value: {
      type: STRING,
      description: 'The value associated with the menu item.',
    },
    wrappedContent: {
      type: BOOLEAN,
      description: 'If true, wraps the yielded content in a <span> for additional styling control.',
    },
  },
  resources: {
    github:
      'https://github.com/simplepractice/ember-simplepractice/blob/staging/addon/src/components/spds/core/menu/item.gjs',
  },
};

export default class SpdsCoreMenuItem extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-menu-item';
  classesToMapToArgs = ['hidden'];
  styles = styles;

  @tracked isSelected = 'false';

  @reads('baseElement.ariaSelected') ariaSelected;

  get keepSelectedState() {
    return this.ariaSelected === 'true' && this.args.retainSelection;
  }

  get showSelectedStyles() {
    return (
      this.args.showSelectedStyles ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'showSelectedStyles')
    );
  }

  get role() {
    return this.args.role ?? getDefaultValueFromMetadata(this.constructor.metadata, 'role');
  }

  @action
  onClick() {
    if (this.showSelectedStyles && !this.keepSelectedState) {
      this.isSelected = `${this.ariaSelected === 'false'}`;
    }

    this.args.onClick(...arguments);
  }

  <template>
    <Button
      @flex="1"
      @gap="0"
      aria-current={{this.isSelected}}
      aria-selected={{this.isSelected}}
      class={{this.classes}}
      data-test-spds-menu-item
      data-value={{@value}}
      id={{this.id}}
      role={{this.role}}
      type="button"
      ...attributes
      {{this.setupComponent}}
      {{this.validateArguments}}
      {{on "click" this.onClick}}
    >
      {{#if @wrappedContent}}
        <span>{{yield}}</span>
      {{else}}
        {{yield}}
      {{/if}}
    </Button>
  </template>
}

export const SPDS_CORE_MENU_ITEM_METADATA = SpdsCoreMenuItem.metadata;
